import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  get,
  find,
  isEmpty,
  noop,
} from 'lodash';
import findCurrentGroup from 'common/utils/findCurrentGroup';
import determineLanguageDifferences from 'common/utils/determineLanguageDifferences';
import { isFocused, INSURANCE } from 'common/utils/isFocused';
import {
  createUpdateDeletePaymentsInsurances,
} from 'src/components/Insurance/utils/';
import {
  createInsuranceEntry,
  fetchPaymentsInsurance,
  fetchInsurancePlanInformation,
  deleteInsuranceEntry,
  editInsuranceEntry,
} from 'src/components/Insurance/actions';
import {
  createGroupContact,
  createContactAddress,
  deleteContactAddressField,
  updateContactAddressField,
  updateGroupContact,
  createContactPreferences,
  updateContactPreferences,
  createRecordLanguage,
  deleteRecordLanguage,
} from 'actions/Contact/Group';
/* eslint-disable-next-line max-len */
import featureFlag from 'common/utils/FeatureFlag/FeatureFlag';
import AddContactInformationForm from './AddContactInformationForm';
import {
  decodeContactData,
} from '../utils';

const LOCATION_ADDRESS_FIELDS = ['line_1', 'city', 'state', 'postal_code', 'country'];
const NON_LOCATION_ADDRESS_FIELDS = ['address_type', 'line_2', 'is_mailing_address', 'is_primary'];
const COORDINATE_ADDRESS_FIELDS = ['latitude', 'longitude'];
const UNITED_STATES_VALUES = new Set([
  'us',
  'usa',
  'united states',
  'united states of america',
]);

function normalizeAddressValue(field, value) {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value !== 'string') {
    return value;
  }

  const normalized = value.trim();

  if (field === 'country') {
    const country = normalized.toLowerCase();
    return UNITED_STATES_VALUES.has(country) ? 'US' : country;
  }

  return normalized;
}

function hasChangedAddressFields(currentAddress = {}, newAddress = {}, fields = []) {
  return fields.some((field) => (
    normalizeAddressValue(field, currentAddress[field]) !== normalizeAddressValue(field, newAddress[field])
  ));
}

function hasGeocodedSelection(address = {}) {
  return Boolean(address.latitude && address.longitude && address.city && address.state);
}

function hasChangedAddressCoordinates(currentAddress = {}, newAddress = {}) {
  return hasGeocodedSelection(newAddress) &&
    hasChangedAddressFields(currentAddress, newAddress, COORDINATE_ADDRESS_FIELDS);
}

function shouldUpdateAddress(currentAddress = {}, newAddress = {}) {
  const hasLocationChanges = hasChangedAddressFields(currentAddress, newAddress, LOCATION_ADDRESS_FIELDS);

  if (hasLocationChanges) {
    return hasGeocodedSelection(newAddress);
  }

  if (hasChangedAddressCoordinates(currentAddress, newAddress)) {
    return true;
  }

  return hasChangedAddressFields(currentAddress, newAddress, NON_LOCATION_ADDRESS_FIELDS);
}

function shouldGeocodeAddressUpdate(currentAddress = {}, newAddress = {}) {
  return hasChangedAddressFields(currentAddress, newAddress, LOCATION_ADDRESS_FIELDS);
}

function categorizeAddressChanges(currentAddresses, newAddresses) {
  const currentIds = new Set(currentAddresses.map((a) => a.id).filter(Boolean));
  const newIds = new Set(newAddresses.map((a) => a.id).filter(Boolean));
  const currentAddressById = currentAddresses.reduce((acc, address) => ({
    ...acc,
    ...(address.id ? { [address.id]: address } : {}),
  }), {});

  return {
    toDelete: currentAddresses.filter((a) => a.id && !newIds.has(a.id)),
    toUpdate: newAddresses
      .filter((address) => address.id && currentIds.has(address.id))
      .filter((address) => shouldUpdateAddress(currentAddressById[address.id], address))
      .map((address) => ({
        address,
        geocode: shouldGeocodeAddressUpdate(currentAddressById[address.id], address),
      })),
    toAdd: newAddresses.filter((a) => !a.id || !currentIds.has(a.id)),
  };
}

function hasAddressChanges({ toDelete, toUpdate, toAdd }) {
  return toDelete.length > 0 || toUpdate.length > 0 || toAdd.length > 0;
}

function buildAddressPromises({ toDelete, toUpdate, toAdd }, groupId, contactId, actions) {
  return [
    ...toDelete.map((addr) => actions.deleteAddress(groupId, contactId, addr.id)),
    ...toUpdate.map(({ address, geocode }) => (
      actions.updateAddress(groupId, contactId, address.id, address, { geocode })
    )),
    ...toAdd.map((addr) => actions.createAddress(groupId, contactId, addr)),
  ];
}

export class AddContactInformation extends Component {
  constructor(props) {
    super(props);
    this.createOrUpdateContact = this.createOrUpdateContact.bind(this);
  }

  componentDidMount() {
    const { currentGroup } = this.props;
    const insuranceFocused = isFocused(currentGroup, INSURANCE);

    if (insuranceFocused) {
      this.fetchPaymentsData();
    }
  }

  async handleAddressUpdates(contactId, values) {
    const { groupId, selectedContact } = this.props;
    const currentAddresses = isEmpty(selectedContact) ? [] : (selectedContact.addresses || []);
    const newAddresses = values.addresses || [];
    const changes = categorizeAddressChanges(currentAddresses, newAddresses);

    if (!hasAddressChanges(changes)) {
      return;
    }

    await Promise.all(buildAddressPromises(changes, groupId, contactId, {
      deleteAddress: this.props.deleteContactAddressField,
      updateAddress: this.props.updateContactAddressField,
      createAddress: this.props.createContactAddress,
    }));
  }

  async handleContactPreferences(contactId, values) {
    // Always process contact_preferences if they exist
    const { selectedContact } = this.props;
    const contactPreferenceId = selectedContact?.contact_preference?.id;
    const contactPreferencesNewValues = values?.contact_preferences;

    if (!contactPreferencesNewValues) {
      return true;
    }

    if (contactPreferencesNewValues?.voice_mail_ok !== undefined) {
      contactPreferencesNewValues.voice_mail_ok = contactPreferencesNewValues.voice_mail_ok === 'true';
    }

    if (contactPreferenceId) {
      const updateResult = await updateContactPreferences(contactPreferenceId, contactPreferencesNewValues);
      return updateResult !== false;
    }

    const createResult = await createContactPreferences(contactId, contactPreferencesNewValues);
    return createResult !== false;
  }

  /* eslint-disable react/sort-comp, class-methods-use-this */
  // Language-related helper methods grouped together for maintainability
  getActualContactId(contactId, selectedContact) {
    return contactId || selectedContact?.id;
  }

  buildRecordLanguagesValues(values) {
    return {
      preferred_spoken_languages: values?.preferred_spoken_languages || [],
      preferred_written_languages: values?.preferred_written_languages || [],
    };
  }

  hasLanguageFieldChanges(modifiedFields) {
    const languageFields = ['preferred_spoken_languages', 'preferred_written_languages'];
    return languageFields.some((field) => field in modifiedFields);
  }

  validateLanguagesAvailable(allLanguages) {
    if (!allLanguages || allLanguages.length === 0) {
      // eslint-disable-next-line no-console
      console.error('Languages not available. Cannot update language preferences.');
      return false;
    }
    return true;
  }

  async handleLanguageUpdates(modifiedFields, values, allLanguages = [], contactId = null) {
    if (!this.hasLanguageFieldChanges(modifiedFields)) {
      return;
    }

    // Languages should be provided from ContactRequirementsForm via component state
    if (!this.validateLanguagesAvailable(allLanguages)) {
      return;
    }

    const { selectedContact } = this.props;
    const actualContactId = this.getActualContactId(contactId, selectedContact);

    // Use preferred_languages from Redux state (already fresh after updateGroupContact)
    const existingRecordLanguages = selectedContact?.preferred_languages || [];
    const recordLanguagesNewValues = this.buildRecordLanguagesValues(values);

    const { languagesToAdd, languagesToRemove } = determineLanguageDifferences(
      existingRecordLanguages,
      recordLanguagesNewValues,
      allLanguages,
    );

    await Promise.all(
      languagesToAdd.map((language) => {
        const recordLanguage = {
          ...language,
          record_type: 'Person',
          record_id: actualContactId,
        };
        return createRecordLanguage(recordLanguage);
      }),
    );

    await Promise.all(
      languagesToRemove.map((recordLanguage) => deleteRecordLanguage(recordLanguage.id)),
    );
  }
  /* eslint-enable react/sort-comp, class-methods-use-this */

  async updateExistingContact(values, insurances, initialPaymentsIds, allLanguages = []) {
    const { groupId, location: { query: { contactId } } } = this.props;

    await this.handleAddressUpdates(contactId, values);

    // For contact preferences, always process if they exist (RFF doesn't track dirty state the same way)
    const contactPreferencesResult = await this.handleContactPreferences(contactId, values);
    if (!contactPreferencesResult) return false;

    const showNotification = false;
    const payload = await this.props.updateGroupContact(
      groupId,
      contactId,
      { ...values, addresses: undefined },
    );

    // Always attempt language updates for existing contacts
    // Build a modifiedFields object that includes language fields if they have values
    const modifiedFields = {};
    if (values.preferred_spoken_languages) {
      modifiedFields.preferred_spoken_languages = values.preferred_spoken_languages;
    }
    if (values.preferred_written_languages) {
      modifiedFields.preferred_written_languages = values.preferred_written_languages;
    }
    await this.handleLanguageUpdates(modifiedFields, values, allLanguages, contactId);

    const finalPayload = await this.createUpdateDeletePaysInsurances(
      payload,
      insurances,
      initialPaymentsIds,
      showNotification,
    );

    return finalPayload;
  }

  async createOrUpdateContact(
    values = {},
    insurances = [],
    initialPaymentsIds = [],
    allLanguages = [],
  ) {
    const {
      groupId,
      location: { query: { contactId } },
    } = this.props;

    if (contactId) {
      return this.updateExistingContact(values, insurances, initialPaymentsIds, allLanguages);
    }

    const resolveLanguages = (names = []) => names.map((name) => {
      if (typeof name === 'string') {
        return find(allLanguages, { name }) || { id: name, name };
      }
      return name;
    });

    const contactWithResolvedLanguages = {
      ...values,
      preferred_spoken_languages: resolveLanguages(values.preferred_spoken_languages),
      preferred_written_languages: resolveLanguages(values.preferred_written_languages),
    };

    return this.props.createGroupContact({
      groupId,
      contact: contactWithResolvedLanguages,
      options: {},
    }).then((payload) => this.createUpdateDeletePaysInsurances(
      payload,
      insurances,
      initialPaymentsIds,
    ));
  }

  createUpdateDeletePaysInsurances(
    payload,
    insurances = [],
    initialPaymentsIds = [],
    showNotification,
  ) {
    const { groupId } = this.props;
    const contactId = get(payload, 'data.data.id', {});

    const paymentsIds = initialPaymentsIds.filter((insurance) => insurance.plan_type !== 'social');

    createUpdateDeletePaymentsInsurances({
      initialPaymentsIds: paymentsIds,
      values: { insurance: insurances },
      contactId,
      groupId,
      createInsuranceEntry: this.props.createInsuranceEntry,
      editInsuranceEntry: this.props.editInsuranceEntry,
      deleteInsuranceEntry: this.props.deleteInsuranceEntry,
      showNotification,
    });

    return payload;
  }

  fetchPaymentsData() {
    const { location: { query: { contactId } }, groupId } = this.props;
    if (contactId) {
      this.props.fetchPaymentsInsurance(contactId, groupId)
        .then(({ data: { data = [] } }) => data.forEach(
          (insurance) => this.props.fetchInsurancePlanInformation(insurance.relationships.plan.data.id, contactId),
        ));
    }
  }

  render() {
    const {
      activeStep,
      context,
      contactData,
      insurances,
      integrationContact,
      location: { query: { contactId } },
      selectedContact,
      route,
    } = this.props;

    return (
      <div className="contact-search">
        <AddContactInformationForm
          activeStep={activeStep}
          context={context}
          contactId={contactId}
          contactData={contactData}
          insurances={insurances}
          integrationContact={integrationContact}
          selectedContact={selectedContact}
          createOrUpdateContact={this.createOrUpdateContact}
          checkForAttachedForms={route.from === 'nav' || route.from === 'screening' || route.from === 'referral'}
          isClientRoute={route.from === 'nav'}
        />
      </div>
    );
  }
}

AddContactInformation.propTypes = {
  activeStep: PropTypes.object.isRequired,
  contactData: PropTypes.object.isRequired,
  context: PropTypes.string,
  createGroupContact: PropTypes.func.isRequired,
  createInsuranceEntry: PropTypes.func,
  currentGroup: PropTypes.object.isRequired,
  deleteContactAddressField: PropTypes.func.isRequired,
  deleteInsuranceEntry: PropTypes.func,
  editInsuranceEntry: PropTypes.func,
  fetchInsurancePlanInformation: PropTypes.func,
  fetchPaymentsInsurance: PropTypes.func,
  groupId: PropTypes.string.isRequired,
  integrationContact: PropTypes.bool,
  insurances: PropTypes.array,
  selectedContact: PropTypes.object,
  createContactAddress: PropTypes.func.isRequired,
  updateContactAddressField: PropTypes.func.isRequired,
  updateGroupContact: PropTypes.func.isRequired,
  route: PropTypes.object.isRequired,
  location: PropTypes.shape({
    query: PropTypes.object,
  }).isRequired,
};

AddContactInformation.defaultProps = {
  context: '',
  createInsuranceEntry: noop,
  deleteInsuranceEntry: noop,
  editInsuranceEntry: noop,
  fetchInsurancePlanInformation: noop,
  fetchPaymentsInsurance: noop,
  insurances: [],
  integrationContact: false,
  selectedContact: {},
};

const EMPTY_INSURANCES = [];

export function mapStateToProps(state, ownProps) {
  const {
    session: { groupId },
  } = state;
  const currentGroup = findCurrentGroup(state.user, state.session);
  const {
    location:
    {
      query: {
        contact,
        contactId,
        fromIntegration: integrationContact,
      },
    },
  } = ownProps;

  const contactData = decodeContactData(contact);
  const selectedContact = contactId && find(state.searchedContacts, { id: contactId });
  const fetchedContact = contactId && find(state.contacts.contacts, { id: contactId });

  return {
    contactData,
    currentGroup,
    groupId,
    integrationContact,
    insurances: state.insurances.data[contactId] || EMPTY_INSURANCES,
    selectedContact: fetchedContact || selectedContact,
  };
}

export default connect(
  mapStateToProps,
  {
    createGroupContact,
    createInsuranceEntry,
    deleteContactAddressField,
    deleteInsuranceEntry,
    editInsuranceEntry,
    fetchInsurancePlanInformation,
    fetchPaymentsInsurance,
    updateContactAddressField,
    createContactAddress,
    updateGroupContact,
  },
)(featureFlag(AddContactInformation));
